import type { FC, ReactNode } from 'react'

import { RotateCcw, TriangleAlert } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import EmptyState from '@/components/generals/empty-state'
import PageContainer from '@/components/generals/page-container'

interface PageLoaderProps {
    rows?: number
}

const PageLoader: FC<PageLoaderProps> = ({ rows = 6 }): ReactNode => {
    const { status, reload } = useClinic()

    if (status === 'error') {
        return (
            <PageContainer>
                <EmptyState
                    icon={TriangleAlert}
                    title='Patient records could not be opened'
                    description='This browser blocked access to saved records. Check that cookies and site data are allowed, then try again.'
                    action={(
                        <Button onClick={reload}>
                            <RotateCcw />
                            Try again
                        </Button>
                    )}
                />
            </PageContainer>
        )
    }

    return (
        <PageContainer>
            <div className='flex flex-col gap-6' aria-busy='true' aria-label='Loading'>
                <div className='flex flex-col gap-2'>
                    <Skeleton className='h-8 w-56' />
                    <Skeleton className='h-4 w-80 max-w-full' />
                </div>

                <div className='grid gap-4 md:grid-cols-3'>
                    <Skeleton className='h-28 rounded-xl' />
                    <Skeleton className='h-28 rounded-xl' />
                    <Skeleton className='h-28 rounded-xl' />
                </div>

                <div className='flex flex-col gap-3 rounded-xl border bg-background p-4'>
                    {Array.from({ length: rows }, (_, index) => (
                        <Skeleton key={index} className='h-10 w-full' />
                    ))}
                </div>
            </div>
        </PageContainer>
    )
}

export default PageLoader
